import api from "../config/axiosConfig";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as yup from "yup";
import { convertToFormData } from "../config/convertToFormData";
import { actionType } from "../utils/constant";
import { useSelector } from "react-redux";

/**
 * Validation for painting template form.
 * - model_id: required (model is locked in edit UI) - skipped when updateFromStage is true
 * - painting_checklist_template_images: 1–15 images
 * - painting_checklist_template_parts: ≥1 part with:
 *   - part_name, part_location (required)
 *   - painting_part_items: ≥1 with item_name (required)
 */
const getValidationSchema = (isEditing, updateFromStage = false) =>
  yup.object({
    model_id: updateFromStage
      ? yup.string()
      : yup.string().required("Model is required"),
    painting_checklist_template_images: yup
      .array()
      .min(1, "At least one image is required")
      .max(15, "Maximum 15 images allowed"),
    painting_checklist_template_parts: yup
      .array()
      .of(
        yup.object().shape({
          part_name: yup.string().required("Part area is required"),
          part_location: yup.string().required("Part location is required"),
          painting_part_items: yup
            .array()
            .of(
              yup.object().shape({
                item_name: yup.string().required("Inspection is required"),
              })
            )
            .min(1, "At least one inspection is required"),
        })
      )
      .min(1, "At least one part is required"),
  });

/**
 * Form controller for Create/Update Painting Template.
 * Responsibilities:
 * - Owns Formik state/validation, model list fetch, and image handling.
 * - Create mode: manages everything locally; submit posts full payload.
 * - Edit mode: fields for parts/subparts auto-save via debounced PUT calls; submit updates the full parts payload.
 * - Models list is sorted by `is_template_created` ascending then `model_name`.
 * - Exposes aggregated flags `checkNewParts` and `checkNewSubparts` to detect any unsaved (no ID) entities.
 * - Prevents closing the modal in edit mode when new parts/subparts exist (to avoid losing unsaved entities).
 * - Image logic: in edit mode, uploads go to API and local state syncs to server response; deletions hit API first, then local removal.
 */
const useCreatePaintingTemplateContent = (
  isEditing,
  onClose,
  initialData,
  onSave,
  open,
  updateFromStage = false
) => {
  const { profile } = useSelector((state) => state.profile);
  const { permissions } = profile;
  // -------------- STATE MANAGEMENT --------------
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [models, setModels] = useState([]);

  // -------------- FORMIK INITIALIZATION --------------
  /** Initialize Formik with initialData or sane defaults. */
  const formik = useFormik({
    initialValues: {
      model_id: initialData?.model_id || "",
      painting_checklist_template_images:
        initialData?.painting_checklist_template_images || [],
      painting_checklist_template_parts:
        initialData?.painting_checklist_template_parts || [
          {
            part_name: "",
            part_location: "",
            painting_part_items: [{ item_name: "" }],
          },
        ],
    },
    validationSchema: getValidationSchema(isEditing, updateFromStage),
    enableReinitialize: true,
    onSubmit: async (values) => {
      await handleSubmit(values);
    },
  });

  // -------------- DATA FETCHING --------------
  /** Fetch models when modal opens (used for create). Skip when updateFromStage. */
  useEffect(() => {
    if (open && !updateFromStage) {
      fetchModels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, updateFromStage]);

  /** Fetch available models from API. */
  const fetchModels = async () => {
    try {
      const response = await api.get(
        "/api/painting-checklist-templates/model/list"
      );
      const raw = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      const sorted = [...raw].sort((a, b) => {
        const aVal = Boolean(a?.is_template_created);
        const bVal = Boolean(b?.is_template_created);
        // false (0) first, true (1) later
        if (aVal !== bVal) return Number(aVal) - Number(bVal);
        // secondary by model_name for stable UX
        return String(a?.model_name || "").localeCompare(
          String(b?.model_name || "")
        );
      });
      setModels(sorted);
    } catch (error) {
      console.error("Error fetching models:", error);
      toast.error("Failed to fetch models");
    }
  };

  // -------------- NEW/EXISTING CHECK FLAGS --------------
  const { checkNewParts, checkNewSubparts } = useMemo(() => {
    const parts = formik?.values?.painting_checklist_template_parts || [];
    let hasNewParts = false;
    let hasNewSubparts = false;

    for (const part of parts) {
      const partPersisted = Boolean(part?.painting_checklist_part_id);
      if (!partPersisted) {
        hasNewParts = true;
        // no need to inspect its subparts; they're implicitly new scope
      } else if (Array.isArray(part?.painting_part_items)) {
        for (const sp of part.painting_part_items) {
          if (!sp?.painting_checklist_part_item_id) {
            hasNewSubparts = true;
            break;
          }
        }
      }
      if (hasNewParts && hasNewSubparts) break;
    }

    return { checkNewParts: hasNewParts, checkNewSubparts: hasNewSubparts };
  }, [formik?.values?.painting_checklist_template_parts]);

  // -------------- SERVER DELETE HELPERS --------------
  /** Delete a template part (item) on server by id. */
  const handleDeletePartAPI = async (id) => {
    if (!permissions.actions.includes(actionType.template_delete)) {
      toast.error("You don't have permission to delete parts.");
      return false;
    }
    try {
      await api.delete(`/api/painting-checklist/part/${id}`);
      toast.success("Part deleted successfully");
      return true;
    } catch (error) {
      console.error("Error deleting part:", error);
      toast.error("Failed to delete part from server");
      return false;
    }
  };

  /** Delete a template inspection (part-item) on server by id. */
  const handleDeleteSubpartAPI = async (id) => {
    if (!permissions.actions.includes(actionType.template_delete)) {
      toast.error("You don't have permission to delete parts.");
      return false;
    }
    try {
      await api.delete(`/api/painting-checklist/part-item/${id}`);
      toast.success("Item Part Inspection deleted successfully");
      return true;
    } catch (error) {
      console.error("Error deleting item part inspection:", error);
      toast.error("Failed to delete item part inspection from server");
      return false;
    }
  };

  // -------------- DEBOUNCED SERVER UPDATES --------------
  /**
   * Debounce maps for part and subpart updates so we don't spam API while typing
   */
  const partUpdateTimers = useRef({});
  const subpartUpdateTimers = useRef({});

  /**
   * Schedule a debounced PUT to update a Part field on the server.
   * Triggers only in edit mode and when the part has a server id.
   * Usage: call after changing either part_name or part_location.
   *
   * @param {number} itemIndex - Index of the part in formik values
   * @param {('part_name'|'part_location')} field - Which field changed
   * @param {number} [delay=500]
   */
  const debouncePartUpdate = (itemIndex, field, newValue, delay = 500) => {
    if (!isEditing) return;
    if (!permissions.actions.includes(actionType.template_edit)) {
      toast.error("You don't have permission to update parts.");
      return false;
    }
    const partsNow = formik.values.painting_checklist_template_parts || [];
    const partNow = partsNow[itemIndex];
    const partId = partNow?.painting_checklist_part_id;
    if (!partId) return;

    // Use a composite key so name/location timers don't clobber each other
    const timerKey = `${partId}:${field}`;

    // Clear any existing timer for this part-field
    if (partUpdateTimers.current[timerKey]) {
      clearTimeout(partUpdateTimers.current[timerKey]);
    }

    partUpdateTimers.current[timerKey] = setTimeout(async () => {
      try {
        // Resolve the most recent value: prefer provided newValue; otherwise read from latest Formik state
        let payload = {};
        if (field === "part_name") {
          const current =
            typeof newValue !== "undefined"
              ? newValue
              : formik.values.painting_checklist_template_parts?.[itemIndex]
                  ?.part_name || "";
          payload = { part_name: current };
        } else if (field === "part_location") {
          const current =
            typeof newValue !== "undefined"
              ? newValue
              : formik.values.painting_checklist_template_parts?.[itemIndex]
                  ?.part_location || "";
          payload = { part_location: current };
        } else return;

        await api.put(`/api/painting-checklist/part/${partId}`, payload);
        // Silent success to avoid toast spam while typing
      } catch (error) {
        console.error("Error updating part:", error);
        toast.error("Failed to update part");
      }
    }, delay);
  };

  /**
   * Schedule a debounced PUT to update a Subpart (inspection) on the server.
   * Triggers only in edit mode and when the subpart has a server id.
   * Usage: call after changing item_name, visual_check, or remark.
   *
   * @param {number} itemIndex
   * @param {number} subPartIndex
   * @param {number} [delay=500]
   */
  const debounceSubpartUpdate = (
    itemIndex,
    subPartIndex,
    newValue,
    delay = 500
  ) => {
    if (!isEditing) return;
    if (!permissions.actions.includes(actionType.template_edit)) {
      toast.error("You don't have permission to update inspections.");
      return false;
    }
    const partsNow = formik.values.painting_checklist_template_parts || [];
    const subNow = partsNow[itemIndex]?.painting_part_items?.[subPartIndex];
    const subId = subNow?.painting_checklist_part_item_id;
    if (!subId) return;

    const timerKey = `${subId}:item_name`;

    if (subpartUpdateTimers.current[timerKey]) {
      clearTimeout(subpartUpdateTimers.current[timerKey]);
    }

    subpartUpdateTimers.current[timerKey] = setTimeout(async () => {
      try {
        const current =
          typeof newValue !== "undefined"
            ? newValue
            : formik.values.painting_checklist_template_parts?.[itemIndex]
                ?.painting_part_items?.[subPartIndex]?.item_name || "";
        const payload = { item_name: current };
        await api.put(`/api/painting-checklist/part-item/${subId}`, payload);
      } catch (error) {
        console.error("Error updating inspection:", error);
        toast.error("Failed to update inspection");
      }
    }, delay);
  };

  // -------------- INITIAL DATA HANDLING --------------
  /** Seed images from initialData in edit mode (avoid extra validation). */
  useEffect(() => {
    const imgs = initialData?.painting_checklist_template_images || [];
    if (!imgs.length) return;

    // Only update if different to prevent re-render loops
    const current = formik.values.painting_checklist_template_images || [];
    const isSame =
      current.length === imgs.length && current.every((v, i) => v === imgs[i]);

    if (!isSame) {
      setImageFiles(imgs);
      // avoid triggering extra validation on initialization
      formik.setFieldValue("painting_checklist_template_images", imgs, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // -------------- FORM SUBMISSION --------------
  /** Submit handler: build FormData, call API (create/update), toast + notify parent. */
  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      // Special handling for updateFromStage mode - just pass data to parent
      if (updateFromStage) {
        if (onSave) {
          await onSave(values);
        }
        setIsSubmitting(false);
        return;
      }

      let response;
      if (isEditing) {
        if (!permissions.actions.includes(actionType.template_edit)) {
          toast.error("You don't have permission to update templates.");
          return false;
        }
        // Update existing painting template
        // Only send:
        // - Entire parts when the part has NO id (new parts)
        // - For existing parts (with id), ONLY the subparts that have NO id (new subparts)
        const formatedPainting_checklist_template_parts = (
          values.painting_checklist_template_parts || []
        )
          .map((part) => {
            const hasPartId = Boolean(part?.painting_checklist_part_id);
            const subparts = Array.isArray(part?.painting_part_items)
              ? part.painting_part_items
              : [];

            if (!hasPartId) {
              // New part: send the whole part with all subparts
              return {
                part_name: part?.part_name || "",
                part_location: part?.part_location || "",
                painting_part_items: subparts.map((sub) => ({
                  // For creation of new subparts under a new part, only item_name is needed
                  item_name: sub?.item_name || "",
                })),
              };
            }

            // Existing part: filter to only new subparts (those without an id)
            const newSubparts = subparts
              .filter((sub) => !sub?.painting_checklist_part_item_id)
              .map((sub) => ({ item_name: sub?.item_name || "" }));

            if (newSubparts.length === 0) return null;

            return {
              painting_checklist_part_id: part.painting_checklist_part_id,
              painting_part_items: newSubparts,
            };
          })
          .filter(Boolean);

        const editId = initialData?.painting_checklist_template_id;
        const payload = {
          painting_checklist_template_parts:
            formatedPainting_checklist_template_parts,
        };
        response = await api.put(
          `/api/painting-checklist-templates/${editId}`,
          payload
        );
        toast.success("Painting template updated successfully!");
      } else {
        if (!permissions.actions.includes(actionType.template_create)) {
          toast.error("You don't have permission to create templates.");
          return false;
        }
        // Create new painting template
        const payload = {
          model_id: values.model_id,
          images: values.painting_checklist_template_images || [],
          painting_checklist_template_parts:
            values.painting_checklist_template_parts,
        };
        const formData = convertToFormData(payload);
        response = await api.post(
          `/api/painting-checklist-templates/`,
          formData
        );
        toast.success("Painting template created successfully!");
      }

      // Let parent handle closing/resetting and list refresh
      if (onSave) onSave(response?.data);
    } catch (error) {
      console.error("Error saving painting template:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save painting template"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------- MODAL HANDLING --------------
  /** Close modal: reset form, clear images, bubble up. */
  const handleClose = () => {
    if (isEditing && (checkNewParts || checkNewSubparts)) {
      toast.error("Please save the changes before closing the modal");
      return;
    }
    formik.resetForm();
    setImageFiles([]);
    onClose();
  };

  /** Update model_id from Autocomplete selection. */
  const handleModalChange = (event, newValue) => {
    formik.setFieldValue("model_id", newValue?.model_id || "");
  };

  // -------------- IMAGE HANDLING --------------
  /** Merge newly picked files into images (supports event or files[]). Upload to server first in edit mode. */
  const handleImageUpload = async (eventOrFiles) => {
    // Normalize input to an array of File objects
    let filesToAdd;
    if (eventOrFiles?.target?.files) {
      filesToAdd = Array.from(eventOrFiles.target.files);
    } else if (Array.isArray(eventOrFiles)) {
      filesToAdd = eventOrFiles;
    } else {
      console.error("Invalid input to handleImageUpload", eventOrFiles);
      return;
    }

    // Enforce local validation limit (this hook uses max 15 images)
    const current = formik.values.painting_checklist_template_images || [];
    if (current.length + filesToAdd.length > 15) {
      formik.setFieldError(
        "painting_checklist_template_images",
        "Maximum 15 images allowed"
      );
      return;
    }

    // If editing, push to server first, then update local state from server response
    const templateId = initialData?.painting_checklist_template_id;
    if (isEditing && templateId) {
      try {
        const payload = {
          painting_checklist_template_id: templateId,
          images: filesToAdd,
        };
        const formData = convertToFormData(payload);

        // Upload only the newly added files
        const res = await api.post(
          `/api/painting-checklist-templates/images`,
          formData
        );
        // Accept either data.images or data.data.images
        const serverImages = res?.data?.data || [];

        // Update both local previews and Formik to the canonical server list
        setImageFiles(serverImages);
        formik.setFieldValue(
          "painting_checklist_template_images",
          serverImages
        );
        toast.success("Images uploaded successfully");
      } catch (err) {
        console.error("Error uploading images:", err);
        formik.setFieldError(
          "painting_checklist_template_images",
          "Failed to upload images"
        );
        // Do not mutate local state if server upload failed
      }
      return;
    }

    // Create mode: update locally; images will be sent on create submit
    const updatedImages = [...imageFiles, ...filesToAdd];
    setImageFiles(updatedImages);
    formik.setFieldValue("painting_checklist_template_images", updatedImages);
  };

  /** Remove image by index (supports (event, idx) or direct idx). */
  const removeImage = async (eventOrIndex, actualIndex) => {
    // Support both (event, index) and (index) call patterns
    let indexToRemove;
    const evt = typeof actualIndex !== "undefined" ? eventOrIndex : null;

    if (typeof actualIndex !== "undefined") {
      indexToRemove = actualIndex;
      if (evt?.stopPropagation) evt.stopPropagation();
    } else {
      indexToRemove = eventOrIndex;
    }

    const current = formik.values.painting_checklist_template_images || [];
    const imageToRemove = current[indexToRemove];
    const isFileObject = imageToRemove instanceof File;
    const serverImageId = imageToRemove?.image_id;

    // If editing and image exists on server, delete on server first
    if (isEditing && !isFileObject && serverImageId) {
      try {
        await api.delete(`/api/painting-checklist/images/${serverImageId}`);
        toast.success("Image deleted successfully");
      } catch (err) {
        console.error("Error deleting image:", err);
        toast.error("Failed to delete image from server");
        return; // abort local removal on failure
      }
    }

    // Proceed with local removal (for new files or after successful server delete)
    const updatedImages = imageFiles.filter(
      (_, index) => index !== indexToRemove
    );
    setImageFiles(updatedImages);
    formik.setFieldValue("painting_checklist_template_images", updatedImages);
  };

  // -------------- ITEM MANAGEMENT --------------
  /** Add a new item (part) to the template. */
  const addItem = () => {
    const newItem = {
      part_name: "",
      part_location: "",
      painting_part_items: [{ item_name: "" }],
    };
    const currentItems = formik.values.painting_checklist_template_parts || [];
    formik.setFieldValue("painting_checklist_template_parts", [
      ...currentItems,
      newItem,
    ]);
  };

  /**
   * Remove an item (part) from the template
   *
   * @param {number} itemIndex - Index of item to remove
   */
  const removeItem = async (itemIndex) => {
    const currentItems = formik.values.painting_checklist_template_parts || [];

    // If editing and server id exists, call delete API first
    const part = currentItems[itemIndex];
    const partId = part?.painting_checklist_part_id;

    if (isEditing && partId) {
      const ok = await handleDeletePartAPI(partId);
      if (!ok) return; // abort local removal on failure
    }

    const updatedItems = currentItems.filter((_, index) => index !== itemIndex);
    formik.setFieldValue("painting_checklist_template_parts", updatedItems);
  };

  // -------------- SUB-PART MANAGEMENT --------------
  /**
   * Add a new sub-part (inspection) to a specific item
   *
   * @param {number} itemIndex - Index of parent item
   */
  const addSubPart = (itemIndex) => {
    const currentItems = [
      ...(formik.values.painting_checklist_template_parts || []),
    ];
    if (currentItems[itemIndex]) {
      const currentSubParts = currentItems[itemIndex].painting_part_items || [];
      currentItems[itemIndex].painting_part_items = [
        ...currentSubParts,
        { item_name: "" },
      ];
      formik.setFieldValue("painting_checklist_template_parts", currentItems);
    }
  };

  /**
   * Remove a sub-part (inspection) from a specific item
   *
   * @param {number} itemIndex - Index of parent item
   * @param {number} subPartIndex - Index of sub-part to remove
   */
  const removeSubPart = async (itemIndex, subPartIndex) => {
    const currentItems = [
      ...(formik.values.painting_checklist_template_parts || []),
    ];
    if (
      currentItems[itemIndex] &&
      currentItems[itemIndex].painting_part_items
    ) {
      const partItem =
        currentItems[itemIndex].painting_part_items[subPartIndex];
      const partItemID = partItem?.painting_checklist_part_item_id;

      if (isEditing && partItemID) {
        const ok = await handleDeleteSubpartAPI(partItemID);
        if (!ok) return; // abort local removal on failure
      }

      const updatedSubParts = currentItems[
        itemIndex
      ].painting_part_items.filter((_, index) => index !== subPartIndex);
      currentItems[itemIndex].painting_part_items = updatedSubParts;
      formik.setFieldValue("painting_checklist_template_parts", currentItems);
    }
  };

  // -------------- EXPORTED INTERFACE --------------
  return {
    // Form state
    formik,
    isSubmitting,
    imageFiles,
    models,

    // Modal handlers
    handleClose,
    handleModalChange,

    // Image handlers
    handleImageUpload,
    removeImage,

    // Item handlers
    addItem,
    removeItem,

    // Sub-part handlers
    addSubPart,
    removeSubPart,

    // Debounced updates
    debouncePartUpdate,
    debounceSubpartUpdate,

    // Aggregate new checks
    checkNewParts,
    checkNewSubparts,
  };
};

export default useCreatePaintingTemplateContent;
